/*
 * Brooks Brothers — Popup Banner
 *
 * Custom element: <popup-banner>
 *
 * A promotional popup that is gated on three things, in this order:
 *   1. Frequency cap  — has the shopper already dismissed it inside the window?
 *   2. Cookie consent — the Consentmo dialog must be gone before we arm anything.
 *   3. Trigger        — immediate, time delay, or scroll depth.
 *
 * Config (all read from data-* on the host in connectedCallback):
 *   data-handle             storage key for this placement          (default 'default')
 *   data-trigger-type       immediate | delay | scroll              (default 'immediate')
 *   data-trigger-delay      seconds, when trigger-type=delay        (default 0)
 *   data-trigger-scroll     percent 0-100, when trigger-type=scroll (default 50)
 *   data-frequency          once_per_session | once_ever | every_n_days
 *                                                                   (default 'once_per_session')
 *   data-frequency-days     window length, when frequency=every_n_days (default 7)
 *   data-consent-selector   override for the consent dialog selector
 *
 * DOM hooks (children of the host):
 *   [data-dismiss]        click closes; the attribute value is the recorded
 *                         reason (e.g. data-dismiss="backdrop")
 *   [data-cta-click]      click closes with reason 'cta'
 *   [data-popup-dialog]   the dialog panel. Needs tabindex="-1" so it can take
 *                         programmatic focus. Focus is trapped inside it while
 *                         open, and Escape closes it.
 *
 * Recorded reasons: 'close' | 'backdrop' | 'cta'. Escape records 'close'.
 *
 * The host MUST render with the `hidden` attribute — this component only ever
 * clears it, it never sets the initial hidden state.
 *
 * Consentmo promotes its host to the top layer (popover="manual" +
 * showPopover()), so no z-index this popup can pick will ever paint above it.
 * That is the right outcome — the consent dialog should always win — but it
 * means the gate below, not CSS, is the only thing keeping the two apart.
 *
 * Storage: a { [handle]: { dismissedAt, reason } } map so several popups can
 * share one key. The .v1 suffix reserves room for a schema migration.
 *
 * NOTE: in the theme editor (Shopify.designMode) the frequency cap and the
 * consent gate are both bypassed so the merchant always sees the popup, and
 * dismissing writes nothing to storage.
 */

(function () {
    'use strict';

    var SECONDS_PER_DAY = 86400;

    // Consentmo injects its UI at runtime from an app embed, so there is no
    // static selector in the theme to key off.
    //
    // Verified against the live bundle (gdpr-backpack-828): Consentmo appends a
    // custom element <csm-cookie-consent> to document.body and renders the whole
    // banner INSIDE its shadow root (attachShadow mode 'open'). Two consequences
    // drive everything below:
    //   - document.querySelector('.isense-cc-window') is ALWAYS null — the class
    //     lives across a shadow boundary, which querySelector does not pierce.
    //     So we look up the host in the light DOM, then step into .shadowRoot.
    //   - MutationObserver does not cross that boundary either, so observing
    //     document.body never sees the banner appear or leave. We observe the
    //     shadow root itself once it exists.
    //
    // data-consent-selector overrides the HOST selector, not the inner banner.
    var DEFAULT_CONSENT_SELECTOR = 'csm-cookie-consent, .csm-cookie-consent';

    // The banner inside the shadow root. Consentmo is the former iSense GDPR app
    // and still stamps Osano cookieconsent classes: `cc-window isense-cc-window
    // cc-type-opt-in ...`. The layout modifier (.cc-banner / .cc-floating /
    // .cc-modern) is deliberately NOT matched, so a merchant switching banner
    // layout in the Consentmo dashboard does not break the gate.
    var CONSENT_BANNER_SELECTOR = '.isense-cc-window, .cc-window';

    // How long to wait for Consentmo to inject its host before concluding there
    // is no consent UI on this page. The app embed loads async, so "host not
    // found" on first paint means "not injected yet", NOT "already accepted" —
    // resolving straight away would race the banner onto the screen.
    var CONSENT_GRACE_MS = 3000;

    // Is the consent banner out of the way?
    //
    // Osano hides via `.cc-invisible`, which sets opacity:0 and NOTHING else —
    // the element stays in the DOM, stays display:flex, stays position:fixed.
    // That rules out both obvious checks: offsetParent is null for ANY
    // position:fixed element (reports "gone" while the banner is on screen), and
    // display never becomes 'none' (reports "still there" forever). So test
    // every way it can actually be hidden, and lead with the class.

    function isConsentGone(hostSelector) {
        var host = document.querySelector(hostSelector);
        if (!host) return true;

        // Host is in the DOM but has not attached its shadow root yet — the
        // banner is coming, so the gate must hold.
        if (!host.shadowRoot) return false;

        var banner = host.shadowRoot.querySelector(CONSENT_BANNER_SELECTOR);
        if (!banner) return true;
        if (banner.classList.contains('cc-invisible')) return true;

        var style = window.getComputedStyle(banner);
        return (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            Number(style.opacity) === 0
        );
    }

    // The theme ships its own consent banner in snippets/cookie-banner.liquid,
    // entirely separate from the Consentmo app embed. On a store where Consentmo
    // is installed but not rendering (its shadow root exists yet stays empty),
    // watching only Consentmo reports "consent is gone" while the theme banner
    // is plainly on screen — and since .m-cookie-banner is z-index 9999 against
    // this popup's 400, the shopper sees both stacked. So gate on both.
    var THEME_BANNER_ID = 'm-cookie-banner';

    // Three-valued on purpose. The element is server-rendered and starts at
    // display:none, so "not visible right now" does NOT mean "will never show":
    // cookie-banner.liquid calls showBanner() only after Shopify.loadFeatures
    // resolves customerPrivacy. Answering 'gone' during that window is exactly
    // the race that lets the popup out early.
    //   'showing'   — on screen, hold the gate
    //   'gone'      — the theme has decided it will not show
    //   'undecided' — consent state not resolved yet; only the grace timer
    //                 may downgrade this to 'gone'
    function themeBannerState() {
        var el = document.getElementById(THEME_BANNER_ID);
        if (!el) return 'gone';

        if (el.classList.contains('show')) return 'showing';

        var style = window.getComputedStyle(el);
        if (style.display !== 'none' && style.visibility !== 'hidden') return 'showing';

        // Same source of truth initCookieBanner() uses: anything other than
        // 'no_interaction' means the shopper already chose, so it never shows.
        try {
            var privacy = window.Shopify && window.Shopify.customerPrivacy;
            if (privacy && typeof privacy.getTrackingConsent === 'function') {
                return privacy.getTrackingConsent() === 'no_interaction' ? 'undecided' : 'gone';
            }
        } catch (e) {
            // customerPrivacy not loaded yet
        }

        return 'undecided';
    }

    // Both consent UIs must be clear. graceElapsed lets the caller decide
    // whether an unresolved theme banner still counts as blocking.
    function isConsentClear(hostSelector, graceElapsed) {
        if (!isConsentGone(hostSelector)) return false;

        var state = themeBannerState();
        if (state === 'showing') return false;
        if (state === 'undecided') return graceElapsed;
        return true;
    }

    // Storage helpers
    // Every access is wrapped — quota errors and private mode must degrade to
    // "not suppressed", never throw.

    function readRecords(storage, key) {
        try {
            var raw = storage.getItem(key);
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            return {};
        }
    }

    function writeRecord(storage, key, handle, record) {
        try {
            var all = readRecords(storage, key);
            all[handle] = record;
            storage.setItem(key, JSON.stringify(all));
        } catch (e) {
            // Quota, disabled, or private-mode — silently skip
        }
    }

    // Tab-reachable descendants, in DOM order. Same list otp-modal.js traps on.
    var FOCUSABLE_SELECTOR =
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Reads a numeric data-* attribute, falling back when absent or unparseable.

    function readNumber(value, fallback) {
        if (value === '' || value == null) return fallback;
        var parsed = Number(value);
        return isNaN(parsed) ? fallback : parsed;
    }

    class PopupBanner extends HTMLElement {
        connectedCallback() {
            var self = this;

            // Re-entry guard. connectedCallback fires on every insertion, and the
            // theme editor re-inserts sections constantly; without this the
            // listeners below stack up on one element.
            if (this._wired) return;
            this._wired = true;

            // Teardowns registered here are drained by disconnectedCallback.
            // Critical for the theme editor case where sections are removed
            // and re-added repeatedly — without this, the MutationObservers
            // in waitForConsent leak and keep firing check() on every DOM
            // mutation for the rest of the session.
            this._teardowns = [];

            this.handle = this.dataset.handle || 'default';
            this.triggerType = this.dataset.triggerType || 'immediate';
            this.triggerDelay = readNumber(this.dataset.triggerDelay, 0);
            this.triggerScroll = readNumber(this.dataset.triggerScroll, 50);
            this.frequency = this.dataset.frequency || 'once_per_session';
            this.frequencyDays = readNumber(this.dataset.frequencyDays, 7);
            this.consentSelector = this.dataset.consentSelector || DEFAULT_CONSENT_SELECTOR;

            this.isDesignMode = !!(window.Shopify && window.Shopify.designMode);

            // Frequency short-circuit — nothing else runs, the banner stays hidden
            if (!this.isDesignMode && this.isSuppressedByFrequency()) return;

            // Wire dismiss before arming, so a popup that reveals immediately is
            // never un-dismissable.
            this.querySelectorAll('[data-dismiss]').forEach(function (el) {
                el.addEventListener('click', function () {
                    self.dismiss(el.dataset.dismiss || 'close');
                });
            });

            this.querySelectorAll('[data-cta-click]').forEach(function (el) {
                el.addEventListener('click', function () {
                    self.dismiss('cta');
                });
            });

            this._dialog = this.querySelector('[data-popup-dialog]');
            this._focusBeforeOpen = null;

            // Escape closes — scoped to this popup while it is actually open, so
            // several popups on a page never fight over one key press. Recorded
            // as 'close', same as the X button: keyboard and pointer are two
            // routes to one intent, and the reason vocabulary stays at three.
            // Torn down deterministically in disconnectedCallback; the
            // self-unbind fallback below stays as belt-and-suspenders for the
            // edge case where disconnectedCallback doesn't fire (e.g., worker
            // detach paths in older browsers).
            function onKeydown(e) {
                if (!self.isConnected) {
                    document.removeEventListener('keydown', onKeydown);
                    return;
                }
                if (e.key === 'Escape' && !self.hidden) {
                    e.preventDefault();
                    self.dismiss('close');
                }
            }
            document.addEventListener('keydown', onKeydown);
            this._teardowns.push(function () {
                document.removeEventListener('keydown', onKeydown);
            });

            // Focus trap — the dialog claims aria-modal="true", so Tab must not
            // be able to walk out of it into the page behind.
            if (this._dialog) {
                this._dialog.addEventListener('keydown', function (e) {
                    if (e.key !== 'Tab') return;

                    var focusable = Array.prototype.slice.call(
                        self._dialog.querySelectorAll(FOCUSABLE_SELECTOR)
                    );
                    if (!focusable.length) return;

                    var first = focusable[0];
                    var last = focusable[focusable.length - 1];

                    // reveal() focuses the dialog itself (tabindex="-1"), which is
                    // neither first nor last — without it in this test, the very
                    // first Shift+Tab after opening walks focus out of the dialog
                    // and into the page behind, contradicting aria-modal="true".
                    if (e.shiftKey && (document.activeElement === first || document.activeElement === self._dialog)) {
                        e.preventDefault();
                        last.focus();
                    } else if (!e.shiftKey && document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                });
            }

            // Consent must clear before the trigger listener even attaches. If
            // the element was disconnected while we were waiting (theme editor
            // rebuild, JS route change), do nothing — armTrigger on a detached
            // element registers listeners on nothing and holds more closures.
            this.waitForConsent().then(function () {
                if (!self.isConnected) return;
                self.armTrigger();
            });
        }

        disconnectedCallback() {
            // Run every teardown registered during connectedCallback. Anything
            // async that outlives the element's presence in the DOM must clean
            // up here — MutationObservers in waitForConsent, the grace timer,
            // and any scroll listener attached by armTrigger.
            while (this._teardowns && this._teardowns.length) {
                try { this._teardowns.pop()(); } catch (e) {}
            }
        }

        // Frequency cap — read side

        isSuppressedByFrequency() {
            var record;

            if (this.frequency === 'once_per_session') {
                record = readRecords(sessionStorage, PopupBanner.SESSION_KEY)[this.handle];
                return !!record;
            }

            if (this.frequency === 'once_ever') {
                record = readRecords(localStorage, PopupBanner.STORAGE_KEY)[this.handle];
                return !!record;
            }

            if (this.frequency === 'every_n_days') {
                record = readRecords(localStorage, PopupBanner.STORAGE_KEY)[this.handle];
                if (!record || typeof record.dismissedAt !== 'number') return false;
                var elapsed = Math.floor(Date.now() / 1000) - record.dismissedAt;
                return elapsed < this.frequencyDays * SECONDS_PER_DAY;
            }

            return false;
        }

        // Consent gate

        waitForConsent() {
            var self = this;

            return new Promise(function (resolve) {
                if (self.isDesignMode) return resolve();

                var bodyObserver = null;
                var shadowObserver = null;
                var graceTimer = null;
                var graceElapsed = false;

                function done() {
                    if (bodyObserver) bodyObserver.disconnect();
                    if (shadowObserver) shadowObserver.disconnect();
                    clearTimeout(graceTimer);
                    resolve();
                }

                self._teardowns.push(done);

                function check() {
                    if (isConsentClear(self.consentSelector, graceElapsed)) done();
                }

                // The banner leaves from inside the shadow root, which the body
                // observer cannot see — so watch the shadow root directly.
                function watchShadow(host) {
                    if (shadowObserver || !host.shadowRoot) return;

                    // The grace timer deliberately keeps running: it is no longer
                    // just a "no consent UI here" countdown, it is also the only
                    // thing that resolves an 'undecided' theme banner. Cancelling
                    // it because Consentmo showed up would hang the gate forever
                    // on stores where customerPrivacy never loads.
                    shadowObserver = new MutationObserver(check);
                    shadowObserver.observe(host.shadowRoot, {
                        childList: true,
                        subtree: true,
                        attributes: true,
                    });
                    check();
                }

                function watchHost() {
                    var host = document.querySelector(self.consentSelector);
                    if (!host) return;

                    if (host.shadowRoot) {
                        watchShadow(host);
                        return;
                    }

                    // Host element exists but has not attached its shadow root
                    // yet. Consentmo fires this on the host once it has.
                    host.addEventListener(
                        'csm-cookie-consent-ready',
                        function () {
                            watchShadow(host);
                        },
                        { once: true }
                    );
                }

                // Grace window elapsed: async consent UI has had its chance to
                // appear. Re-check rather than resolving outright — resolving
                // blindly here is what let the popup through while the theme
                // banner was on screen.
                graceTimer = setTimeout(function () {
                    graceElapsed = true;
                    check();
                }, CONSENT_GRACE_MS);

                // childList catches the Consentmo host being appended; attributes
                // catches the theme banner flipping class/style, which is how
                // cookie-banner.liquid shows and hides it.
                bodyObserver = new MutationObserver(function () {
                    watchHost();
                    check();
                });
                bodyObserver.observe(document.body, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['class', 'style'],
                });

                watchHost();
                check();
            });
        }

        // Trigger strategies

        armTrigger() {
            var self = this;

            if (this.triggerType === 'delay') {
                // Cancel the timer on disconnect. Without this, a setTimeout
                // scheduled during connectedCallback still fires after the
                // element is torn down — reveal() runs on a detached element,
                // sets hidden = false on nothing, and requests a focus frame
                // that may throw.
                var timer = setTimeout(function () {
                    self.reveal();
                }, this.triggerDelay * 1000);
                this._teardowns.push(function () { clearTimeout(timer); });
                return;
            }

            if (this.triggerType === 'scroll') {
                // Remove the scroll listener on disconnect. The listener
                // self-removes on first fire, but if the element leaves the
                // DOM before that fire (theme editor rebuild, JS route
                // change), the closure stays on window forever holding self
                // in scope — leaks one listener per disconnected instance
                // and fires on every subsequent scroll event.
                var onScroll = function () {
                    var max = document.documentElement.scrollHeight - window.innerHeight;
                    var ratio = max > 0 ? window.scrollY / max : 1;
                    if (ratio * 100 >= self.triggerScroll) {
                        window.removeEventListener('scroll', onScroll);
                        self.reveal();
                    }
                };
                window.addEventListener('scroll', onScroll, { passive: true });
                this._teardowns.push(function () {
                    window.removeEventListener('scroll', onScroll);
                });
                return;
            }

            // immediate, and anything unrecognised
            this.reveal();
        }

        // Visibility

        reveal() {
            // Remember where focus was so dismiss() can hand it back.
            this._focusBeforeOpen = document.activeElement;

            this.hidden = false;
            document.body.classList.add('popup-banner-open');

            // Move focus into the dialog, one frame later — the element is only
            // focusable once it has actually been laid out.
            var dialog = this._dialog;
            if (dialog) {
                requestAnimationFrame(function () {
                    dialog.focus();
                });
            }
        }

        dismiss(reason) {
            this.hidden = true;
            document.body.classList.remove('popup-banner-open');

            // Hand focus back to whatever had it before the popup opened, so a
            // keyboard user does not get dumped at the top of the document.
            var previous = this._focusBeforeOpen;
            this._focusBeforeOpen = null;
            if (previous && typeof previous.focus === 'function') {
                previous.focus();
            }

            // Never poison the merchant's storage while previewing in the editor
            if (this.isDesignMode) return;

            var record = {
                dismissedAt: Math.floor(Date.now() / 1000),
                reason: reason,
            };

            if (this.frequency === 'once_per_session') {
                writeRecord(sessionStorage, PopupBanner.SESSION_KEY, this.handle, record);
            } else {
                writeRecord(localStorage, PopupBanner.STORAGE_KEY, this.handle, record);
            }
        }
    }

    PopupBanner.STORAGE_KEY = 'bbs.popup.dismissed.v1';
    PopupBanner.SESSION_KEY = 'bbs.popup.session.v1';

    if (!customElements.get('popup-banner')) {
        customElements.define('popup-banner', PopupBanner);
    }
})();
